// Export service for leads functionality
import { optimizedApi } from '@/utils/api/optimized-api';

export interface ExportOptions {
    export_data?: boolean;
    include_utm_data?: boolean;
    selected_lead_ids?: string[];
    selected_columns?: string[];
    export_all?: boolean;
    primary_filters?: any;
    custom_filters?: any;
    apiEndpoint?: string; // Custom API endpoint for export (default: '/leads')
}

interface ExportResponse {
    success?: boolean;
    message?: string;
    downloadUrl?: string;
    fileName?: string;
    recordCount?: number;
    total?: number;
    exportedRecords?: number;
    fileUrl?: string;
    download_url?: string;
    data?: {
        download_url?: string;
    };
}

// Transform frontend filters to backend format (matches leads table logic exactly)
// Currently unused but kept for potential future use
/* function _transformFiltersForBackend(filters: any) {
    const transformed: any = {};

    if (filters) {
        Object.keys(filters).forEach(key => {
            const filter = filters[key];

            // Skip if no filter or empty value
            if (!filter || filter.value === undefined || filter.value === null || filter.value === '') {
                return;
            }

            // Handle owner filter - send UUIDs to the backend (same as leads table)
            if (key === 'owner') {
                if (Array.isArray(filter.value) && filter.value.length > 0) {
                    transformed['owner_uuids'] = filter.value;
                }
                return;
            }

            // Handle is_qualified filter - convert string to number (same as leads table)
            if (key === 'is_qualified') {
                transformed[key] = parseInt(filter.value);
                return;
            }

            // Handle date/dateTime filters (same as leads table)
            if (filter.type === 'date' || filter.type === 'dateTime') {
                if (filter.from || filter.to) {
                    transformed[key] = {
                        from: filter.from,
                        to: filter.to
                    };
                }
            }
            // Handle multi-select filters (same as leads table)
            else if (filter.type === 'select' && Array.isArray(filter.value) && filter.value.length > 0) {
                transformed[key] = filter.value;
            }
            // Handle text filters (same as leads table)
            else if (!Array.isArray(filter.value) && filter.value !== '') {
                transformed[key] = filter.value;
            }
        });
    }

    return transformed;
} */

export async function exportLeads(options: ExportOptions): Promise<string> {
    try {
        console.log('Export options received:', options);
        console.log('Selected columns:', options.selected_columns);

        // Build payload for instant download export
        const payload: any = {
            primary_filters: options.primary_filters || {},
            custom_filters: options.custom_filters || {},
            export_data: true, // Flag to indicate this is an export request
            include_utm_data: options.include_utm_data || false,
            export_all: options.export_all || false
        };

        // Always include selected_columns if provided
        if (options.selected_columns && options.selected_columns.length > 0) {
            payload.selected_columns = options.selected_columns;
            console.log('Added selected_columns to payload:', options.selected_columns);
        }

        // Handle selected leads export - send UUIDs directly
        if (options.selected_lead_ids && options.selected_lead_ids.length > 0) {
            console.log('Exporting selected leads (UUIDs):', options.selected_lead_ids);
            // Send UUIDs directly to backend - let backend handle the conversion
            payload.selected_lead_uuids = options.selected_lead_ids;
        } else {
            console.log('No selected lead IDs, exporting with current filters');
        }

        console.log('Export API payload:', payload);

        // Use optimizedApi for consistency with rest of app
        const endpoint = options.apiEndpoint || '/leads';
        console.log('Export API endpoint:', endpoint);
        const response = await optimizedApi.post<ExportResponse>(endpoint, payload);
        console.log('Export API response:', response);

        // Backend now returns downloadUrl for instant download
        // Response comes wrapped in ApiResponse, so access via response.data
        const exportData = response.data || response as any;

        if (exportData.downloadUrl) {
            console.log(`Export ready! ${exportData.recordCount} records, URL: ${exportData.downloadUrl}`);
            return exportData.downloadUrl; // Return CDN URL for instant download
        }

        // If no downloadUrl, export failed
        throw new Error(response.message || exportData.message || 'Export failed - no download URL received');
    } catch (error) {
        console.error('Export error:', error);
        throw new Error(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

// Download file from URL
export function downloadFile(url: string, filename?: string) {
    // For cross-origin URLs (like DigitalOcean Spaces), the download attribute doesn't work
    // reliably due to CORS restrictions. Instead, open the URL directly which will trigger
    // the browser's download behavior based on Content-Disposition headers from the CDN.

    // Check if URL is cross-origin
    const urlObj = new URL(url);
    const isCrossOrigin = urlObj.origin !== window.location.origin;

    if (isCrossOrigin) {
        // For cross-origin URLs, open directly in new tab
        // The CDN should be configured to send Content-Disposition: attachment header
        // which will trigger download in browser
        window.open(url, '_blank');
    } else {
        // For same-origin URLs, use download attribute
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        if (filename) {
            link.download = filename;
        }
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}